// src/components/cabling/CablingImpactMetrics.jsx
import React from 'react';
import { COLORS } from '../../constants';

// SVG Icon Components
const ProgressIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9h-4m-7 9A9 9 0 0 1 3 12m9 9v-4M3 12a9 9 0 0 1 9-9m-9 9h4m7-9a9 9 0 0 0-9 9m9-9v4" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const RemainingIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const AtRiskIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    <path d="M12 22V12" />
  </svg>
);

const CriticalIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Mini progress ring component
const MiniProgressRing = ({ value, size = 40, color }) => {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={COLORS.line}
        strokeWidth={3}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={3}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x={size / 2}
        y={size / 2 + 4}
        textAnchor="middle"
        fontSize={size * 0.25}
        fill={color}
        fontWeight="bold"
      >
        {Math.round(value)}%
      </text>
    </svg>
  );
};

export default function CablingImpactMetrics({ copperData, fibreData }) {
  const totalCopperQty = copperData?.reduce((sum, c) => sum + (Number(c.qty) || 0), 0) || 0;
  const totalFibreQty = fibreData?.reduce((sum, f) => sum + (Number(f.qty) || 0), 0) || 0;
  
  const copperInstalled = copperData?.reduce((sum, c) => sum + (Number(c.roughedIn) || 0), 0) || 0;
  const fibreInstalled = fibreData?.reduce((sum, f) => sum + (Number(f.completed) || 0), 0) || 0;
  
  const totalCables = totalCopperQty + totalFibreQty;
  const totalInstalled = copperInstalled + fibreInstalled;
  const totalRemaining = totalCables - totalInstalled;
  const overallProgress = totalCables > 0 ? (totalInstalled / totalCables) * 100 : 0;
  
  const copperProgress = totalCopperQty > 0 ? (copperInstalled / totalCopperQty) * 100 : 0;
  const fibreProgress = totalFibreQty > 0 ? (fibreInstalled / totalFibreQty) * 100 : 0;
  
  const atRiskProjects = copperData?.filter(c => c.status === 'In Progress' && (c.progress || 0) < 50).length || 0;
  const atRiskFibre = fibreData?.filter(f => f.status === 'In Progress' && (f.progress || 0) < 50).length || 0;
  const totalAtRisk = atRiskProjects + atRiskFibre;
  
  const delayedProjects = copperData?.filter(c => c.remaining > 0 && (c.progress || 0) < 30).length || 0;
  const delayedFibre = fibreData?.filter(f => f.remaining > 0 && (f.progress || 0) < 30).length || 0;
  const totalDelayed = delayedProjects + delayedFibre;

  const metrics = [
    {
      label: 'Overall Progress',
      value: `${Math.round(overallProgress)}%`,
      change: `${Math.round(totalInstalled)}/${totalCables} installed`,
      color: overallProgress >= 80 ? COLORS.success : overallProgress >= 50 ? COLORS.warning : COLORS.danger,
      icon: <ProgressIcon />,
      ring: <MiniProgressRing value={overallProgress} size={50} color={overallProgress >= 80 ? COLORS.success : overallProgress >= 50 ? COLORS.warning : COLORS.danger} />,
    },
    {
      label: 'Remaining Work',
      value: totalRemaining.toLocaleString(),
      change: `${Math.round((totalRemaining / totalCables) * 100)}% of total`,
      color: totalRemaining > 0 ? COLORS.warning : COLORS.success,
      icon: <RemainingIcon />,
    },
    {
      label: 'At Risk',
      value: totalAtRisk,
      change: `${Math.round((totalAtRisk / (copperData?.length + fibreData?.length || 1)) * 100)}% of projects`,
      color: totalAtRisk > 0 ? COLORS.danger : COLORS.success,
      icon: <AtRiskIcon />,
    },
    {
      label: 'Critical Delay',
      value: totalDelayed,
      change: 'Needs immediate attention',
      color: totalDelayed > 0 ? COLORS.danger : COLORS.success,
      icon: <CriticalIcon />,
    },
  ];

  const avgProgress = (copperProgress + fibreProgress) / 2;
  const copperVsFibre = copperProgress > fibreProgress ? 'Copper ahead' : 'Fibre ahead';
  const progressDiff = Math.abs(copperProgress - fibreProgress).toFixed(1);

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 20,
          marginBottom: 24,
        }}
      >
        {metrics.map((metric, idx) => (
          <div
            key={idx}
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 20,
              borderLeft: `4px solid ${metric.color}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ color: metric.color }}>{metric.icon}</div>
              {metric.ring && <div>{metric.ring}</div>}
              {!metric.ring && (
                <span style={{ fontSize: 11, fontWeight: 600, color: metric.color }}>
                  {metric.label === 'Remaining Work' ? 'Pending' : metric.label === 'Overall Progress' ? 'Complete' : ''}
                </span>
              )}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: metric.color, marginBottom: 4 }}>
              {metric.value}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
              {metric.label}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
              {metric.change}
            </div>
          </div>
        ))}
      </div>

      {/* Mini comparison bar */}
      <div
        style={{
          background: COLORS.surface,
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 24,
          border: `1px solid ${COLORS.line}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS.primary }} />
            <span style={{ fontSize: 12, fontWeight: 500 }}>Copper</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>{Math.round(copperProgress)}%</span>
          </div>
          <div style={{ width: 100, height: 4, background: COLORS.line, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${copperProgress}%`, height: '100%', background: COLORS.primary, borderRadius: 2 }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS.cyan }} />
            <span style={{ fontSize: 12, fontWeight: 500 }}>Fibre</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.cyan }}>{Math.round(fibreProgress)}%</span>
          </div>
          <div style={{ width: 100, height: 4, background: COLORS.line, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${fibreProgress}%`, height: '100%', background: COLORS.cyan, borderRadius: 2 }} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
          <span style={{ fontWeight: 700, color: copperProgress > fibreProgress ? COLORS.primary : COLORS.cyan }}>
            {copperVsFibre}
          </span>
          {' by '}{progressDiff}% · Overall avg {Math.round(avgProgress)}%
        </div>
      </div>
    </>
  );
}